/**
 * Finds the line in the shape that is intersected by the perpendicular from the midpoint of mainLine.
 * @param {Object} mainLine - The reference line {from: {x, y}, to: {x, y}}.
 * @param {Object[]} lines - Array of lines in the shape [{from: {x, y}, to: {x, y}}, ...].
 * @returns {Object|null} - The intersecting line or null if none found.
 */
import {getMidPoint} from "../../../../../helpers/sqftHelper";

function getLineAngle(line) {
  return Math.atan2(line.to.y - line.from.y, line.to.x - line.from.x);
}

function createPerpendicularThroughPoint(point, angle) {
  const normalAngle = angle + Math.PI / 2;
  return {
    from: { x: point.x - Math.cos(normalAngle) * 10000, y: point.y - Math.sin(normalAngle) * 10000 },
    to: { x: point.x + Math.cos(normalAngle) * 10000, y: point.y + Math.sin(normalAngle) * 10000 },
  };
}

export function findNearestOppositeLineV3(mainLine, lines) {
  const OFFSET = 0.1;
  const ANGLE_TOLERANCE = Math.PI / 12;

  const angle = getLineAngle(mainLine);
  const lineLength = Math.hypot(mainLine.to.x - mainLine.from.x, mainLine.to.y - mainLine.from.y);
  const mainMid = getMidPoint(mainLine.from, mainLine.to);

  const dirX = (mainLine.to.x - mainLine.from.x) / lineLength;
  const dirY = (mainLine.to.y - mainLine.from.y) / lineLength;

  const points = [
    { x: mainLine.from.x + dirX * OFFSET, y: mainLine.from.y + dirY * OFFSET },
    mainMid,
    { x: mainLine.to.x - dirX * OFFSET, y: mainLine.to.y - dirY * OFFSET }
  ];

  const intersections = [];

  points.forEach(pt => {
    const perp = createPerpendicularThroughPoint(pt, angle);
    let closestIntersection = null;
    let minDistance = Infinity;

    lines.forEach(line => {
      if (line === mainLine) return;

      const intersection = getIntersection(perp, line);
      if (intersection) {
        const lineAngle = getLineAngle(line);
        if (Math.abs(lineAngle - angle) > ANGLE_TOLERANCE && Math.abs(Math.abs(lineAngle - angle) - Math.PI) > ANGLE_TOLERANCE) {
          return;
        }

        const distance = Math.hypot(intersection.x - pt.x, intersection.y - pt.y);
        if (distance < minDistance) {
          minDistance = distance;
          closestIntersection = { line, distance };
        }
      }
    });

    if (closestIntersection) intersections.push(closestIntersection);
  });

  if (intersections.length > 0) {
    intersections.sort((a, b) => a.distance - b.distance);
    return intersections[0].line;
  }

  // If there are no intersections, return the closest parallel line
  let closestParallelLine = null;
  let minMidDistance = Infinity;

  lines.forEach(line => {
    if (line === mainLine) return;

    const lineAngle = getLineAngle(line);
    if (Math.abs(lineAngle - angle) > ANGLE_TOLERANCE && Math.abs(Math.abs(lineAngle - angle) - Math.PI) > ANGLE_TOLERANCE) {
      return;
    }

    const lineMid = getMidPoint(line.from, line.to);
    const midDistance = Math.hypot(lineMid.x - mainMid.x, lineMid.y - mainMid.y);

    if (midDistance < minMidDistance) {
      minMidDistance = midDistance;
      closestParallelLine = line;
    }
  });

  return closestParallelLine;
}


export function findNearestOppositeLineV2(mainLine, lines) {
  // 1. Compute the midpoint of the main line
  const midX = (mainLine.from.x + mainLine.to.x) / 2;
  const midY = (mainLine.from.y + mainLine.to.y) / 2;

  // 2. Compute the perpendicular direction
  const dx = mainLine.to.x - mainLine.from.x;
  const dy = mainLine.to.y - mainLine.from.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length === 0) return null; // Avoid division by zero

  const normalX = -dy / length;
  const normalY = dx / length;

  // 3. Define the infinite perpendicular line passing through the midpoint
  const perpLine = {
    from: { x: midX - normalX * 10000, y: midY - normalY * 10000 },
    to: { x: midX + normalX * 10000, y: midY + normalY * 10000 }
  };

  let oppositeLine = null;
  let maxDistance = Infinity;

  // 4. Check intersection with each line in the shape
  for (const line of lines) {
    const intersection = getIntersection(perpLine, line);
    if (intersection && line !== mainLine) {
      // Compute distance from mainLine midpoint to intersection point
      const distance = Math.hypot(intersection.x - midX, intersection.y - midY);
      if (distance < maxDistance) {
        maxDistance = distance;
        oppositeLine = line;
      }
    }
  }

  return oppositeLine;
}

/**
 * Finds the closest opposite line to a given base line from an array of lines.
 * Instead of searching only for parallel lines, it finds the nearest line that is "in front" of the base line.
 *
 * @param {Object} baseLine - The base line (resizing line).
 * @param {Array} allLines - Array of all lines in the shape.
 * @returns {Object|null} The closest opposite line, or null if not found.
 */
export function findNearestOppositeLineV1(baseLine, allLines) {
  let minDist = Infinity;
  let nearestLine = null;

  // Get midpoint of the base line
  const baseMid = getMidPoint(baseLine.from, baseLine.to);

  // Get normal vector to the base line
  const dx = baseLine.to.x - baseLine.from.x;
  const dy = baseLine.to.y - baseLine.from.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return null; // Avoid division by zero
  const normal = { x: -dy / len, y: dx / len };

  for (const line of allLines) {
    if (line === baseLine) continue; // Skip the resizing line itself

    // Compute midpoint of the candidate line
    const otherMid = getMidPoint(baseLine.from, baseLine.to);

    // Compute projection distance along the normal
    const dxOther = otherMid.x - baseMid.x;
    const dyOther = otherMid.y - baseMid.y;
    const normalDist = Math.abs(dxOther * normal.x + dyOther * normal.y);

    if (normalDist < minDist) {
      minDist = normalDist;
      nearestLine = line;
    }
  }

  return nearestLine;
}

/**
 * Checks if two lines intersect and returns the intersection point.
 * @param {Object} line1 - First line {from: {x, y}, to: {x, y}}.
 * @param {Object} line2 - Second line {from: {x, y}, to: {x, y}}.
 * @returns {Object|null} - The intersection point {x, y} or null if no intersection.
 */
function getIntersection(line1, line2) {
  const { from: A, to: B } = line1;
  const { from: C, to: D } = line2;

  const det = (B.x - A.x) * (D.y - C.y) - (B.y - A.y) * (D.x - C.x);
  if (det === 0) return null; // Parallel lines

  const t = ((C.x - A.x) * (D.y - C.y) - (C.y - A.y) * (D.x - C.x)) / det;
  const u = ((C.x - A.x) * (B.y - A.y) - (C.y - A.y) * (B.x - A.x)) / det;

  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return { x: A.x + t * (B.x - A.x), y: A.y + t * (B.y - A.y) };
  }

  return null;
}


/**
 * Finds the index of a point in an array by its id.
 * @param {Array} points - Array of points, each with an "id" property.
 * @param {string|number} id - The id to search for.
 * @returns {number} The index of the point with the given id, or -1 if not found.
 */
function findIndexOfPointById(points, id) {
  return points.findIndex(pt => pt.id === id);
}

/**
 * Returns points from startIndex to endIndex, forward or backward, optionally also returns the rest
 * @param {Array} points - array of point objects [{ id, x, y }]
 * @param {number} startIndex - starting index
 * @param {number} endIndex - ending index
 * @param {Object} options - configuration
 * @param {boolean} [options.backward=false] - if true, go backward instead of forward
 * @param {boolean} [options.withRest=false] - if true, return also the remaining points
 * @returns {Object} - { selected: Array, rest?: Array }
 */
function getPointsBetween(points, startIndex, endIndex, options = {}) {
  const { backward = false, withRest = false } = options;
  const selected = [];
  const usedIndexes = new Set();

  const length = points.length;
  let index = startIndex;

  // Build the selected path
  while (true) {
    selected.push(points[index]);
    usedIndexes.add(index);

    if (index === endIndex) break;

    if (backward) {
      index = (index - 1 + length) % length;
    } else {
      index = (index + 1) % length;
    }
  }

  // Build the rest if requested
  const rest = withRest
    ? points.filter((_, i) => !usedIndexes.has(i))
    : undefined;

  return withRest ? { selected, rest } : { selected };
}

//const MIN_LINE_LENGTH = 1;
/**
 * Expands the shape symmetrically by moving points perpendicularly to the mid-point of the resizing line.
 *
 * @param {Object} shape - Shape with array of points: shape.points = [{x, y}, ...]
 * @param {Object} resizingLine - Reference line with .from and .to points
 * @param {number} diff - Total expansion distance
 */
export function expandLineLeftAndRightParts(shape, resizingLine, diff) {
  const points = shape.getPoints();
  const allLines = shape.getLines();

  // 2.Calculate the vector of the line (from to)
  let dx = resizingLine.to.x - resizingLine.from.x;
  let dy = resizingLine.to.y - resizingLine.from.y;
  const len = Math.sqrt(dx * dx + dy * dy);

  if (len === 0) return; // Prevent division by 0

  // 3. Normalize the direction of the line vector (dx, dy)
  dx /= len;
  dy /= len;

  const offset = diff / 2;

  const oppositeLine = findNearestOppositeLineV3(resizingLine, allLines);
  if (!oppositeLine) {
    console.warn("No suitable opposite line found.");
    return;
  }

  const resToIndex = findIndexOfPointById(points, resizingLine.to.id);
  const oppFromIndex = findIndexOfPointById(points, oppositeLine.from.id);
  const splitPoints = getPointsBetween(points, resToIndex, oppFromIndex, { withRest: true });

  const lineToOppFromPoints = splitPoints.selected;
  const lineFromOppToPoints = splitPoints.rest;

  const movedPoints = [];

  // Move and record actual deltas
  lineToOppFromPoints.forEach(point => {
    const deltaX = dx * offset;
    const deltaY = dy * offset;

    point.x += deltaX;
    point.y += deltaY;

    movedPoints.push({ point, deltaX, deltaY });
  });

  lineFromOppToPoints.forEach(point => {
    const deltaX = -dx * offset;
    const deltaY = -dy * offset;

    point.x += deltaX;
    point.y += deltaY;

    movedPoints.push({ point, deltaX, deltaY });
  });

  // // Check lengths after move
  // const hasShortLine = allLines.some(line => {
  //   const lineLen = Math.hypot(
  //     line.to.x - line.from.x,
  //     line.to.y - line.from.y
  //   );
  //   return lineLen < MIN_LINE_LENGTH;
  // });
  //
  // // Revert if any short lines
  // if (hasShortLine) {
  //   movedPoints.forEach(({ point, deltaX, deltaY }) => {
  //     point.x -= deltaX;
  //     point.y -= deltaY;
  //   });
  //   console.warn("Resize blocked: line would be too short");
  //   return;
  // }

  // Adjust overlays correctly
  movedPoints.forEach(({ point, deltaX, deltaY }) => {
    const line = point.getParent();

    if (line.isWithBumpout && line.isWithBumpout()) {
      line.bumpoutOverlay.moveBumpout(-deltaX, -deltaY);
    }
    if (line.isWithMitered && line.isWithMitered()) {
      line.miteredOverlay.moveMitered(-deltaX, -deltaY);
    }
    if (line.isWithUnderlayment && line.isWithUnderlayment()) {
      line.underlaymentOverlay.moveMitered(-deltaX, -deltaY);
    }
  });
}
